import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  publicFlowRadialGlowStyle,
  publicFlowSurfaceClass,
} from "@/lib/imobdesk-public-theme";

type PublicFlowLayoutProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Shell visual das rotas públicas — gradiente, glow radial e orbes com blur
 * (referência: login `auth-layout` + design-tokens).
 */
export function PublicFlowLayout({ children, className }: PublicFlowLayoutProps) {
  const reducedMotion = useReducedMotion();

  return (
    <div
      className={cn(
        publicFlowSurfaceClass,
        "relative overflow-hidden",
        className,
      )}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={publicFlowRadialGlowStyle}
        aria-hidden
      />

      {/* Orbes com blur — animação suave (login / www); estático se reduced motion */}
      {!reducedMotion ? (
        <>
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -top-32 right-[10%] h-72 w-72 rounded-full bg-indigo-400/30 blur-[80px]"
            initial={{ opacity: 0.35 }}
            animate={{
              opacity: [0.32, 0.48, 0.32],
              scale: [1, 1.06, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-violet-400/20 blur-[88px]"
            initial={{ opacity: 0.28 }}
            animate={{
              opacity: [0.25, 0.4, 0.25],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.8,
            }}
          />
        </>
      ) : (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 right-[10%] h-72 w-72 rounded-full bg-indigo-400/18 blur-[80px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-violet-400/14 blur-[88px]"
          />
        </>
      )}

      <div className="relative z-10 flex min-h-screen w-full flex-col items-center justify-center p-4">
        {children}
      </div>
    </div>
  );
}
